import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../client.js';

/**
 * Bild-/Datei-Anhänge des Scope Statements. Nutzt die vorhandene Anhang-Route
 * der pm-api mit entity_type 'scope_statement' und entity_id = Projekt-ID.
 * Die pm-api kennt den Typ und das Beschreibungsfeld (höchstens 2000 Zeichen);
 * Fehler kommen als { success:false, error:{ code, message } } (400/403/404/413).
 */
export const SCOPE_DESCRIPTION_MAX = 2000;
export const SCOPE_ENTITY_TYPE = 'scope_statement';

export interface ScopeAttachment {
  id: string;
  filename: string;
  mime: string | null;
  size: number | null;
  description: string | null;
  created_at: string;
}

const KEY = (pid: string) => ['projects', pid, 'attachments', SCOPE_ENTITY_TYPE, pid] as const;

export const isImageAttachment = (a: Pick<ScopeAttachment, 'mime'>) => !!a.mime && a.mime.startsWith('image/');

export function useScopeAttachments(projectId: string) {
  return useQuery({
    queryKey: KEY(projectId),
    queryFn: () =>
      apiClient.get<ScopeAttachment[]>(`/api/v1/projects/${projectId}/attachments`, {
        entity_type: SCOPE_ENTITY_TYPE,
        entity_id: projectId,
      }),
    enabled: !!projectId,
  });
}

export function useUploadScopeAttachment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('entity_type', SCOPE_ENTITY_TYPE);
      form.append('entity_id', projectId);
      return apiClient.postForm<ScopeAttachment>(`/api/v1/projects/${projectId}/attachments`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}

export function useUpdateScopeAttachmentDescription(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, description }: { id: string; description: string }) =>
      apiClient.patch<ScopeAttachment>(`/api/v1/projects/${projectId}/attachments/${id}`, {
        description: description.trim().slice(0, SCOPE_DESCRIPTION_MAX) || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}

export function useDeleteScopeAttachment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${projectId}/attachments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId) }),
  });
}

/**
 * Download als Blob (Bilder nicht per <img src>: die Route verlangt den Auth-Header).
 * requestBlob liefert bei Fehlern den rohen JSON-Text; daraus wird die Server-Meldung gezogen.
 */
export async function fetchAttachmentBlob(projectId: string, id: string): Promise<Blob> {
  try {
    return await apiClient.requestBlob('GET', `/api/v1/projects/${projectId}/attachments/${id}/download`);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'BLOB_REQUEST_FAILED') {
      let parsed: { code?: string; message?: string } | undefined;
      try { parsed = (JSON.parse(err.message) as { error?: { code?: string; message?: string } }).error; } catch { /* kein JSON */ }
      if (parsed?.message) throw new ApiError(parsed.code ?? err.code, parsed.message, err.status);
    }
    throw err;
  }
}

export async function downloadScopeAttachment(projectId: string, att: ScopeAttachment): Promise<void> {
  const blob = await fetchAttachmentBlob(projectId, att.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Markdown-Verweis auf ein Bild-Attachment; die Vorschau löst `attachment:<id>` auf. */
export const attachmentMarkdown = (att: Pick<ScopeAttachment, 'id' | 'filename'>) =>
  `![${att.filename.replace(/[[\]]/g, '')}](attachment:${att.id})`;
